import {gql} from '@apollo/client';

import {
  AntibodyDomainResolutionQuery,
  AntibodyDomainResolutionQueryVariables,
} from 'antibodies/registration-table/__generated/AntibodyDomainResolutionQuery';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  RegistrationTableAntibodyDomainsFromExistingBioentityCustomEvalQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {getTypedValueFromBioentitySummary, toStringTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {NO_ACCESS_CHIP_TEXT} from 'ui-library/chips/NoAccessChip';

export const ANTIBODY_DOMAIN_RESOLUTION_QUERY = gql`
  query AntibodyDomainResolutionQuery($antibodyId: StrictString!) @skipAudit @engComponent(name: ANTIBODIES) {
    antibody(antibodyId: $antibodyId) {
      ... on AntibodyNode {
        id
        domains {
          type
          specificity
          entity {
            ... on RegistryItemInterface {
              id
              name
            }
            ... on AaSequenceNode {
              id
              name
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default async function autopopulateAntibodyDomainsFromBioentitySummary(
  query: RegistrationTableAntibodyDomainsFromExistingBioentityCustomEvalQuery
): Promise<ServerResolutionResult> {
  const {bioentityId, columnDefinition} = query;
  const result = await doQuerySafe<AntibodyDomainResolutionQuery, AntibodyDomainResolutionQueryVariables>(
    {query: ANTIBODY_DOMAIN_RESOLUTION_QUERY},
    {
      variables: {antibodyId: bioentityId},
    }
  );
  const antibody = result.antibody;
  if (isInaccessible(antibody)) {
    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(''));
  }
  const columnDomain = antibody.domains.find(
    (domain) =>
      domain.type === columnDefinition.domainType && domain.specificity === columnDefinition.specificity
  );
  if (!columnDomain) {
    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(''));
  }
  if (isInaccessible(columnDomain.entity)) {
    return makeCustomResolutionResultFromTypedValue(
      getTypedValueFromBioentitySummary({
        id: columnDomain.entity.invalidId,
        displayLabel: NO_ACCESS_CHIP_TEXT,
      })
    );
  }
  return makeCustomResolutionResultFromTypedValue(
    getTypedValueFromBioentitySummary({
      id: columnDomain.entity.id,
      displayLabel: columnDomain.entity.name,
    })
  );
}
